import React, { createContext, useContext, useMemo, useEffect, useState, useRef } from "react";
import { io } from "socket.io-client";

const SocketContext = createContext(null);
export const useSocket = () => useContext(SocketContext);

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL

if (!SOCKET_URL) {
  throw new Error(
    "REACT_APP_SOCKET_URL is not set. socket.io-client would silently connect to the " +
    "page's own origin instead of the video call backend. Rebuild the frontend with " +
    "REACT_APP_SOCKET_URL set (e.g. via --build-arg REACT_APP_SOCKET_URL=... in the Docker build)."
  );
}

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
  
    const urlToken = new URLSearchParams(window.location.search).get("token");
    if (urlToken) {
      sessionStorage.setItem("video_token", urlToken);
    }
    const token = urlToken || sessionStorage.getItem("video_token") || null;

    if (!token) {
      console.warn("⚠️ No token found — connecting without auth");
    } else {
      console.log("🔑 Using token for socket auth");
    }

    console.log("🔥 Connecting to socket server:", SOCKET_URL);

    const newSocket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      auth: token ? { token } : {},
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    newSocket.on("connect", () => {
      console.log("✅ Socket connected:", newSocket.id);
      setIsConnected(true);
    });

    newSocket.on("connect_error", (err) => {
      console.error("❌ Socket connection error:", err.message);
      setIsConnected(false);
    });

    newSocket.on("disconnect", (reason) => {
      console.log("🔌 Socket disconnected:", reason);
      setIsConnected(false);
      // Socket.io will auto-reconnect for most disconnect reasons
    });

    newSocket.on("reconnect", (attempt) => {
      console.log("🔄 Socket reconnected after", attempt, "attempt(s)");
      setIsConnected(true);
    });

    newSocket.on("reconnect_error", (err) => {
      console.error("❌ Socket reconnect error:", err.message);
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  const value = useMemo(() => ({
    socket,
    isConnected,
  }), [socket, isConnected]);

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
